import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Briefcase, Award, Star,
  Lock, Calendar, DollarSign, LogOut, Moon, Sun, Settings, Wrench, AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';
import InterviewRequestModal from '../../components/workday-client/InterviewRequestModal';

interface ProfileData {
  id: string;
  first_name: string;
  last_name: string;
  title: string;
  country_region: string;
  experience_years: string;
  workday_modules: string;
  certifications: string;
  experience_text: string;
  availability: string;
  availability_status: string;
  hourly_rate: string;
  photo_url?: string;
  photo_position_x?: number;
  photo_position_y?: number;
  project_end_date?: string;
  resume_url?: string;
}

export default function ConsultantProfileView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { theme, toggleTheme } = useWorkdayClientTheme();
  const isDark = theme === 'dark';

  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [showProUpgradePopup, setShowProUpgradePopup] = useState(false);

  useEffect(() => {
    if (id) fetchProfile();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const channel = supabase
      .channel(`client-profile-view-${id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'consultant_profiles',
        filter: `id=eq.${id}`,
      }, () => { fetchProfile(); })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [id]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('consultant_profiles')
        .select('id, first_name, last_name, title, country_region, experience_years, workday_modules, certifications, experience_text, availability, availability_status, hourly_rate, photo_url, photo_position_x, photo_position_y, project_end_date, resume_url')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (data) {
        setProfile(data);
        if (data.photo_url) {
          if (data.photo_url.startsWith('http')) {
            setPhotoUrl(data.photo_url);
          } else {
            const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(data.photo_url);
            setPhotoUrl(urlData.publicUrl);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/customer/login');
  };

  const modules = profile?.workday_modules
    ? profile.workday_modules.split(',').map(m => m.trim()).filter(Boolean)
    : [];

  const certs = profile?.certifications
    ? profile.certifications.split(',').map(c => c.trim()).filter(Boolean)
    : [];

  const getAvailabilityConfig = (status: string, notice: string) => {
    const noticeText = notice || '';
    switch (status?.toLowerCase()) {
      case 'available':
        return { badge: 'bg-green-100 text-green-800 border-green-200', dot: 'bg-green-500', label: 'Available', desc: noticeText || 'Ready to start a new engagement' };
      case 'limited':
        return { badge: 'bg-yellow-100 text-yellow-800 border-yellow-200', dot: 'bg-yellow-500', label: 'Limited', desc: noticeText || 'May have partial availability' };
      case 'unavailable':
        return { badge: 'bg-red-100 text-red-800 border-red-200', dot: 'bg-red-500', label: 'Unavailable', desc: noticeText || 'Currently on a full-time engagement' };
      case 'on_project':
        return { badge: 'bg-amber-100 text-amber-800 border-amber-200', dot: 'bg-amber-500', label: 'On Project', desc: noticeText || 'Currently engaged on a project' };
      default:
        return { badge: 'bg-gray-100 text-gray-800 border-gray-200', dot: 'bg-gray-400', label: 'Availability Unknown', desc: noticeText || 'Status not yet set' };
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#40E0D0]" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-black text-white' : 'bg-gray-50'}`}>
        <div className="text-center">
          <p className="text-lg font-semibold mb-4">Profile not found</p>
          <button onClick={() => navigate(-1)} className="text-[#40E0D0] hover:underline">Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <WorkdayClientHeader />

      <div className="pt-32 pb-12 max-w-3xl mx-auto px-4 sm:px-6">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center text-[#40E0D0] hover:text-[#40E0D0]/80 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </button>

        {/* Pro restriction banner */}
        <div className={`mb-6 rounded-xl border px-5 py-4 flex items-start gap-3 ${isDark ? 'bg-amber-900/20 border-amber-700/40' : 'bg-amber-50 border-amber-200'}`}>
          <Lock className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className={`text-sm font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
              Limited Profile View
            </p>
            <p className={`text-sm mt-0.5 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
              Upgrade to <strong>Perplexity ATS Pro</strong> to view full name, contact information, and resume access.
            </p>
          </div>
        </div>

        {/* Profile card */}
        <div className={`rounded-2xl shadow-lg overflow-hidden border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
          {/* Header banner */}
          <div className="h-32 bg-gradient-to-r from-[#40E0D0] to-[#0066FF] relative">
            <div className="absolute -bottom-14 left-8">
              {/* Photo is always restricted — show blurred overlay with lock */}
              <div
                className={`relative w-28 h-28 rounded-full border-4 shadow-lg overflow-hidden cursor-pointer ${isDark ? 'border-gray-900' : 'border-white'}`}
                onClick={() => setShowProUpgradePopup(true)}
                title="Upgrade to Pro to view photo"
              >
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt="Consultant"
                    className="w-full h-full object-cover blur-md scale-110"
                    style={{ objectPosition: `${profile.photo_position_x ?? 50}% ${profile.photo_position_y ?? 50}%` }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#40E0D0] to-[#0066FF]" />
                )}
                <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-1">
                  <Lock className="w-7 h-7 text-white" />
                  <span className="text-white text-[9px] font-semibold tracking-wide">PRO</span>
                </div>
              </div>
            </div>
          </div>

          <div className="pt-16 px-8 pb-8">
            {/* Restricted name */}
            <div className="mb-1 flex items-center gap-2">
              <button
                onClick={() => setShowProUpgradePopup(true)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-dashed border-amber-400 bg-amber-50 text-amber-700 text-sm font-medium hover:bg-amber-100 transition-colors"
              >
                <Lock className="w-3.5 h-3.5" />
                Name hidden — Upgrade to Pro
              </button>
            </div>

            {profile.title && (
              <p className={`text-lg font-semibold mt-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{profile.title}</p>
            )}

            {/* Availability */}
            {(() => {
              const avail = getAvailabilityConfig(profile.availability_status, profile.availability);
              return (
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-semibold ${avail.badge}`}>
                    <span className={`w-2 h-2 rounded-full animate-pulse ${avail.dot}`} />
                    {avail.label}
                  </span>
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{avail.desc}</span>
                </div>
              );
            })()}

            {/* Quick stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
              <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Briefcase className="w-4 h-4 text-[#40E0D0]" />
                  <span className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Workday Experience</span>
                </div>
                <p className="font-bold">{profile.experience_years || 'N/A'}</p>
              </div>

              {profile.country_region && (
                <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4 text-[#40E0D0]" />
                    <span className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Region</span>
                  </div>
                  <p className="font-bold">{profile.country_region}</p>
                </div>
              )}

              {profile.hourly_rate && (
                <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className="w-4 h-4 text-[#40E0D0]" />
                    <span className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Rate</span>
                  </div>
                  <p className="font-bold">${profile.hourly_rate}/hr</p>
                </div>
              )}

              {profile.project_end_date && (
                <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-[#40E0D0]" />
                    <span className={`text-xs font-semibold uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Available From</span>
                  </div>
                  <p className="font-bold">{new Date(profile.project_end_date).toLocaleDateString()}</p>
                </div>
              )}
            </div>

            {/* Modules */}
            {modules.length > 0 && (
              <div className="mt-6">
                <h3 className="flex items-center gap-2 text-sm font-semibold mb-3">
                  <Star className="w-4 h-4 text-[#40E0D0]" />
                  Workday Modules
                </h3>
                <div className="flex flex-wrap gap-2">
                  {modules.map((mod, i) => (
                    <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-teal-50 text-teal-800 border border-teal-200'}`}>
                      {mod}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Certifications */}
            {certs.length > 0 && (
              <div className="mt-6">
                <h3 className="flex items-center gap-2 text-sm font-semibold mb-3">
                  <Award className="w-4 h-4 text-[#40E0D0]" />
                  Certifications
                </h3>
                <div className="flex flex-wrap gap-2">
                  {certs.map((cert, i) => (
                    <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-blue-50 text-blue-800 border border-blue-200'}`}>
                      {cert}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Experience text */}
            {profile.experience_text && (
              <div className="mt-6">
                <h3 className="flex items-center gap-2 text-sm font-semibold mb-3">
                  <Briefcase className="w-4 h-4 text-[#40E0D0]" />
                  Experience
                </h3>
                <p className={`text-sm leading-relaxed whitespace-pre-wrap ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{profile.experience_text}</p>
              </div>
            )}

            {/* Contact & resume — restricted */}
            <div className={`mt-6 p-5 rounded-xl border-2 border-dashed ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex items-center gap-3 mb-3">
                <Lock className="w-5 h-5 text-[#40E0D0]" />
                <h3 className="font-semibold">Contact & Resume Access</h3>
              </div>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Full contact details and resume are available to <strong>Perplexity ATS Pro</strong> members only. Upgrade to access direct contact information and download resumes for pre-selected consultants.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowProUpgradePopup(true)}
                  className="flex-1 py-2.5 bg-gradient-to-r from-[#40E0D0] to-[#0066FF] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity text-sm"
                >
                  Upgrade to Pro — Unlock Contact Access
                </button>
                <button
                  onClick={() => setShowInterviewModal(true)}
                  className={`flex-1 py-2.5 font-semibold rounded-xl border text-sm transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                >
                  Request Interview
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Interview request modal */}
      {showInterviewModal && (
        <InterviewRequestModal
          consultant={{
            id: profile.id,
            first_name: profile.first_name,
            last_name: profile.last_name,
            title: profile.title,
            experience_years: profile.experience_years,
            workday_modules: profile.workday_modules,
          }}
          onClose={() => setShowInterviewModal(false)}
          onSuccess={() => { setShowInterviewModal(false); navigate('/workday-client/interview-requests'); }}
        />
      )}

      {/* Pro upgrade popup */}
      {showProUpgradePopup && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[200] p-4"
          onClick={() => setShowProUpgradePopup(false)}
        >
          <div
            className={`max-w-md w-full rounded-2xl shadow-2xl overflow-hidden ${isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-[#40E0D0] to-[#0066FF] p-6 text-white text-center">
              <div className="flex items-center justify-center gap-2 bg-white/15 border border-white/30 rounded-lg px-4 py-2 mb-4 text-sm font-medium">
                <Wrench className="w-4 h-4 flex-shrink-0" />
                <span>Undergoing construction — (Pro) will be available as soon as developments and updates are complete.</span>
              </div>
              <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Lock className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-xl font-bold">Applicant Contact Restricted</h2>
              <p className="text-white/80 text-sm mt-1">This feature requires a Pro account</p>
            </div>
            <div className="p-6 space-y-4">
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Upgrade to <strong>Perplexity ATS Pro</strong> to unlock full applicant contact details including name, email, phone number, and resume access.
              </p>
              <div className={`rounded-xl p-4 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-teal-50 border-teal-100'}`}>
                <div className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-[#40E0D0] flex-shrink-0 mt-0.5" />
                  <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    In <strong>Perplexity ATS Pro</strong> you will still have access to Perplexity's Workday Development contracting and direct-hire placement services.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <button
                  className="w-full py-3 bg-gradient-to-r from-[#40E0D0] to-[#0066FF] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
                  onClick={() => setShowProUpgradePopup(false)}
                >
                  Upgrade to Pro for Full Workday Talent Contact Access
                </button>
                <button
                  className={`w-full py-2.5 text-sm font-medium rounded-xl border transition-colors ${isDark ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                  onClick={() => setShowProUpgradePopup(false)}
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
